/**
 * Centralized role-permission helpers.
 *
 * Hierarchy (low → high authority):
 *   employee < supervisor < manager / team lead / hod / hr
 *           < hr head / director / admin / founder / superadmin
 *
 * Supervisor is a VIEW-ONLY tier: it can read team attendance and team leave
 * records for peers under the same parent manager, but it cannot mutate
 * (approve leaves, edit employees, etc.) and must not appear on manager-only
 * panels (Team Visibility, Employee Management, Documents, Task Management,
 * Work Monitor).
 */

export const SUPERVISOR_ROLE = "supervisor" as const;

export const ELEVATED_ROLES: ReadonlySet<string> = new Set([
  "admin",
  "owner",
  "superadmin",
  "founder",
  "director",
  "hr head",
]);

// Manager tier (can mutate team data — approve leaves, edit, etc.).
// Supervisor is INTENTIONALLY excluded.
export const MANAGER_ROLES: ReadonlySet<string> = new Set([
  "manager",
  "team lead",
  "hod",
  "hr",
]);

// Anyone allowed to view team data read-only (manager tier + supervisor).
export const TEAM_VIEW_ROLES: ReadonlySet<string> = new Set([
  ...MANAGER_ROLES,
  SUPERVISOR_ROLE,
]);

const normalize = (role: string | null | undefined): string =>
  (role ?? "").toLowerCase();

export const isSupervisor = (role: string | null | undefined): boolean =>
  normalize(role) === SUPERVISOR_ROLE;

export const isManagerTier = (role: string | null | undefined): boolean =>
  MANAGER_ROLES.has(normalize(role));

export const isElevated = (role: string | null | undefined): boolean =>
  ELEVATED_ROLES.has(normalize(role));

/** Manager tier OR elevated — i.e., can mutate / approve team data. */
export const canApproveLeaves = (role: string | null | undefined): boolean =>
  isManagerTier(role) || isElevated(role);

/** Manager tier OR elevated — i.e., can create/edit/delete employees. */
export const canManageEmployees = (role: string | null | undefined): boolean =>
  isManagerTier(role) || isElevated(role);

/** Manager tier OR elevated OR supervisor — read-only view of team. */
export const canViewTeamData = (role: string | null | undefined): boolean =>
  TEAM_VIEW_ROLES.has(normalize(role)) || isElevated(role);

/** Pages supervisor must not access at all. */
export const canAccessManagerPanel = (role: string | null | undefined): boolean =>
  isManagerTier(role) || isElevated(role);

/** Alias: manager-tier or elevated — used to gate admin-only UI panels such as the Draft Tasks tab. */
export const isAdminRole = canAccessManagerPanel;
